import { ArticleNonParagraphProps } from "@routes/Article/components/ArticleParagraphElements/utils/types";
import { cn } from "@util/helpers";
import parse, { DOMNode, domToReact, Element } from "html-react-parser";

/**
 * Renders an inline image element in an article.
 * This component processes the body string to extract image and caption elements,
 * applying appropriate styles and classes.
 */
const InlineImage = ({
  bodyString,
  className,
  paragraphWidth,
}: ArticleNonParagraphProps): React.ReactElement => {
  const transformImage = (
    node: DOMNode
  ): string | boolean | void | object | Element | null => {
    if (node.type !== "tag") return node;

    if (node.name === "img") {
      const imgElement = node;
      const removedAttributes = ["class"]; // Remove class as react does not support it directly
      const attributes = Object.fromEntries(
        Object.entries(imgElement.attribs).filter(
          ([key]) => !removedAttributes.includes(key)
        )
      );
      const extraCss = imgElement.attribs.class;

      return (
        <img
          {...attributes}
          src={imgElement.attribs.src}
          alt={imgElement.attribs.alt || ""}
          className={cn(extraCss)}
        />
      );
    }

    if (node.name === "figcaption") {
      const captionElement = node;
      const captionChildren = captionElement.children as DOMNode[];
      const extraCss = captionElement.attribs.class;

      return (
        <figcaption
          className={cn("m-0 mt-1 p-0 text-3xs text-gray-525", extraCss)}
        >
          {domToReact(captionChildren)}
        </figcaption>
      );
    }

    if (node.name === "figure") {
      const figureElement = node;
      const figureChildren = figureElement.children as DOMNode[];
      const extraCss = figureElement.attribs.class;

      return (
        <figure className={cn(extraCss)}>
          {domToReact(figureChildren, { replace: transformImage })}
        </figure>
      );
    }

    return node;
  };

  const transformImageContainer = (node: DOMNode) => {
    if (node.type === "tag" && node.name === "div") {
      const children = node.children as DOMNode[];

      return <>{domToReact(children, { replace: transformImage })}</>;
    }
  };

  return (
    <div
      className={cn("mb-6 whitespace-normal", className, paragraphWidth)}
      data-testid="article-inline-image-component"
    >
      {parse(bodyString, { replace: transformImageContainer })}
    </div>
  );
};

export default InlineImage;
